/**
 * Registo de pilotos anteriores.
 *
 * Um "piloto" é uma campanha passada com janela fechada e um elenco fixo de
 * boias. Os **dados** continuam a vir todos da API — daqui saem só os factos
 * que a base não guarda: quando cada boia entrou na água e quando se calou.
 * Esses instantes vieram da autópsia de 7 set 2026 (temperatura a cair para
 * <21 °C marca a imersão; `window_end` do último lote marca o fim) e são
 * constantes históricas — não se recalculam.
 *
 * Só entram as boias que foram para a água **vivas**. N1 e N5 morreram em
 * terra e N3 nunca ligou; aparecem na nota, não no mapa — um marcador delas
 * seria um "esteve aqui" sobre uma boia que nunca lá esteve.
 *
 * Instantes em ISO 8601 UTC, como tudo o que vem da API. A conversão para o
 * ecrã é de quem desenha, via `lib/tempo.ts`.
 */

export interface BoiaDoPiloto {
  code: string;
  /** Primeiro lote recebido no dia — antes disto a boia estava desligada. */
  inicio: string;
  /** Primeira leitura de água do mar (<21 °C). `null` = nunca entrou na água. */
  imersao: string | null;
  /** `window_end` do último lote recebido. Depois disto, silêncio para sempre. */
  fim: string;
  /** Produziu fixes GNSS durante o piloto. Sem isto desenha-se a planeada, tracejada. */
  gnss: boolean;
}

export interface Piloto {
  id: string;
  rotulo: string;
  /** Janela de consulta à API: cobre do primeiro ao último lote do elenco. */
  de: string;
  ate: string;
  boias: BoiaDoPiloto[];
  /** O que ficou de fora e porquê. Vai escrito no ecrã. */
  nota: string;
}

export const PILOTOS: Piloto[] = [
  {
    id: "apdl-2026-09-05",
    rotulo: "Piloto APDL — 5 set 2026",
    de: "2026-09-05T04:40:00Z",
    ate: "2026-09-05T12:04:00Z",
    boias: [
      {
        code: "N2",
        inicio: "2026-09-05T04:41:05Z",
        imersao: "2026-09-05T10:13:58Z",
        fim: "2026-09-05T12:03:45Z",
        gnss: false,
      },
      {
        code: "N4",
        inicio: "2026-09-05T05:19:20Z",
        imersao: "2026-09-05T10:24:41Z",
        fim: "2026-09-05T10:26:45Z",
        gnss: true,
      },
      {
        code: "N6",
        inicio: "2026-09-05T05:58:40Z",
        imersao: "2026-09-05T10:04:04Z",
        fim: "2026-09-05T10:36:05Z",
        gnss: true,
      },
    ],
    nota:
      "Só as boias que entraram na água vivas. N1 e N5 morreram em terra " +
      "(04:41 e 07:44 locais) e N3 nunca ligou.",
  },
];

export function pilotoPorId(id: string | null): Piloto | null {
  if (id === null) return null;
  return PILOTOS.find((p) => p.id === id) ?? null;
}

/** Fase de uma boia num instante do replay. Cada fase tem rótulo próprio. */
export type FaseBoia = "desligada" | "em-terra" | "na-agua" | "morta";

export function faseNoInstante(boia: BoiaDoPiloto, t: number): FaseBoia {
  if (t < Date.parse(boia.inicio)) return "desligada";
  if (t > Date.parse(boia.fim)) return "morta";
  if (boia.imersao !== null && t >= Date.parse(boia.imersao)) return "na-agua";
  return "em-terra";
}

export const ROTULO_FASE: Record<FaseBoia, string> = {
  desligada: "desligada",
  "em-terra": "ligada, fora de água",
  "na-agua": "na água",
  morta: "morta",
};

/**
 * Último elemento com instante ≤ `t`, ou `null`.
 *
 * `folgaMs` limita a idade: um valor de há duas horas não é "o valor em t",
 * é o último que houve — e no replay essa diferença é precisamente o que se
 * quer ver. Sem folga (posições) devolve o último conhecido, que é o
 * comportamento certo para um marcador num mapa.
 */
export function ultimoAte<T>(
  itens: readonly T[],
  instanteDe: (item: T) => number,
  t: number,
  folgaMs?: number,
): T | null {
  let escolhido: T | null = null;
  for (const item of itens) {
    const ts = instanteDe(item);
    if (ts > t) break;
    escolhido = item;
  }
  if (escolhido === null) return null;
  if (folgaMs !== undefined && t - instanteDe(escolhido) > folgaMs) return null;
  return escolhido;
}
